import { useQueryClient } from '@tanstack/react-query'
import {
  type PaginationState,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { ProgressBar } from 'components/admin/tasks/ProgressBar'
import BulkUpdateTasksDialog from 'components/brokers/tasks/BulkUpdateTasksDialog'
import TasksTable from 'components/brokers/tasks/TasksTable'
import TasksContext from 'components/context/TasksContext'
import { getTasksQueryKey, type TasksFilters } from 'components/hooks/tasks/useGetTasks'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import TaskStatusFilterCards, {
  type TaskStatusStats,
} from 'components/reusable/TaskStatusFilterCards'
import { Button, Select } from 'slab'
import { getFormattedDate } from 'utils'
import {
  getGetBrokersOperationsBatchesBatchIdQueryKey,
  useGetBrokersOperationsBatchesBatchId,
} from 'utils/web_api'

import OperationsBatchActionControls from './OperationsBatchActionControls'
import { StatusBadge } from './StatusBadge'

const MAX_NUMBER_OF_SELECTED_ITEMS = 100

const OperationsBatchDetail = () => {
  const navigate = useNavigate()
  const { id: batchId } = useParams<{ id: string }>()
  const queryClient = useQueryClient()

  const { data: batch, isLoading, isError, error } = useGetBrokersOperationsBatchesBatchId(batchId || '')

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: 20,
  })
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'created_at', desc: true },
  ])
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState(false)

  const numberOfSelectedItems =
    Object.values(rowSelection).filter(Boolean).length

  // Toggle status filter when a card is clicked
  const toggleStatusFilter = (status: string) => {
    setSelectedStatuses((prev) => {
      const newStatuses = prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
      return newStatuses
    })
  }

  const filters: TasksFilters = useMemo(
    () => ({
      operations_batch_ids: [batchId || ''],
      statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    }),
    [batchId, selectedStatuses]
  )

  // Convert batch stats to TaskStatusStats format
  const statusStats: TaskStatusStats = useMemo(() => {
    if (!batch) {
      return {}
    }
    return {
      total: batch.tasks_count,
      completed: batch.tasks_completed_count,
      pending: batch.tasks_pending_count,
      processing: batch.tasks_processing_count,
      failed: batch.tasks_failed_count,
      excluded: batch.tasks_excluded_count,
      canceled: batch.tasks_canceled_count,
    }
  }, [batch])

  if (isError) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <p className='text-red-600 font-semibold'>Error loading batch</p>
          <p className='text-sm text-gray-600 mt-2'>
            {String(error) || 'An unknown error occurred'}
          </p>
          <div className='mt-4'>
            <Button
              onClick={() => navigate('/brokers/operations_batches')}
              variant='secondary'
            >
              Back to Batches
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading || !batch) {
    return (
      <div className='flex items-center justify-center h-64'>
        <LoadingSpinnerCentered />
      </div>
    )
  }

  return (
    <TasksContext.Provider
      value={{
        getTasksQueryKey: getTasksQueryKey({
          filters,
          pagination,
          sorting,
        }),
      }}
    >
      <div className='max-w-[2400px] mx-auto pb-6'>
        <div className='flex-col w-full h-full space-y-3'>
          {/* Header */}
          <div className='bg-white rounded-lg p-6'>
            <div className='flex items-center justify-between mb-4'>
              <div className='flex items-center gap-4'>
                <h1 className='text-2xl font-semibold'>{batch.display_name}</h1>
                <StatusBadge status={batch.status} />
              </div>
              {batch.available_actions && batch.available_actions.length > 0 && (
                <OperationsBatchActionControls
                  batch={batch}
                  onSuccess={() => {
                    // Refresh batch data after action
                    queryClient.invalidateQueries({
                      queryKey: getGetBrokersOperationsBatchesBatchIdQueryKey(batchId || ''),
                    })
                  }}
                />
              )}
            </div>

            {/* Batch Info Grid */}
            <div className='grid grid-cols-1 gap-6 mt-6'>
              <div>
                <h3 className='text-sm font-medium text-gray-500 mb-1'>Period</h3>
                <p className='text-lg'>
                  {getFormattedDate(batch.period_start_date, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    timeZone: 'UTC',
                  })}{' '}
                  -{' '}
                  {getFormattedDate(batch.period_end_date, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    timeZone: 'UTC',
                  })}
                </p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className='mt-6'>
              <h3 className='text-sm font-medium text-gray-500 mb-2'>Progress</h3>
              <ProgressBar
                stats={{
                  total: batch.tasks_count || 0,
                  pending: batch.tasks_pending_count || 0,
                  processing: batch.tasks_processing_count || 0,
                  completed: batch.tasks_completed_count || 0,
                  failed: batch.tasks_failed_count || 0,
                  excluded: batch.tasks_excluded_count || 0,
                  canceled: batch.tasks_canceled_count || 0,
                }}
              />
            </div>

            {/* Task Status Filter Cards */}
            <div className='mt-6'>
              <TaskStatusFilterCards
                stats={statusStats}
                selectedStatuses={selectedStatuses}
                onToggleStatus={toggleStatusFilter}
                isLoading={isLoading}
              />
            </div>
          </div>

          {/* Tasks Table */}
          <div className='bg-white rounded-lg p-4'>
            <div className='flex items-center justify-between mb-4'>
              <h2 className='text-xl font-semibold'>Tasks</h2>
              <div className='flex items-center gap-2'>
                {selectedStatuses.length > 0 && (
                  <Button
                    variant='tertiary-destructive'
                    onClick={() => setSelectedStatuses([])}
                    size='small'
                  >
                    Clear Filters
                  </Button>
                )}
                <Button
                  variant='secondary'
                  onClick={() => setIsBulkUpdateOpen(true)}
                  disabled={
                    numberOfSelectedItems === 0 ||
                    numberOfSelectedItems > MAX_NUMBER_OF_SELECTED_ITEMS
                  }
                  size='small'
                >
                  Bulk Edit ({numberOfSelectedItems})
                </Button>
                <label htmlFor='page-size-select' className='text-sm font-medium text-gray-700'>
                  Per page:
                </label>
                <Select
                  id='page-size-select'
                  value={pagination.pageSize.toString()}
                  onChange={(e) =>
                    setPagination((prev) => ({
                      ...prev,
                      pageSize: parseInt(e.target.value, 10),
                      pageIndex: 1, // Reset to first page when changing page size
                    }))}
                  className='w-20'
                >
                  <option value='10'>10</option>
                  <option value='20'>20</option>
                  <option value='50'>50</option>
                  <option value='100'>100</option>
                </Select>
              </div>
            </div>
            <TasksTable
              filters={filters}
              pagination={pagination}
              setPagination={setPagination}
              sorting={sorting}
              setSorting={setSorting}
              columnVisibilityOverrides={{
                job_type: false,
              }}
              actionsVisibilityOverrides={{
                openInNewTab: true,
                openWorkflow: true,
                openBatchJob: false, // Don't show batch job link - we're already viewing this batch
                openMember: true,
              }}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
            />
          </div>

          {/* Bulk Update Dialog */}
          <BulkUpdateTasksDialog
            isOpen={isBulkUpdateOpen}
            setIsOpen={setIsBulkUpdateOpen}
            selectedItems={Object.keys(rowSelection).filter((key) => rowSelection[key])}
          />
        </div>
      </div>
    </TasksContext.Provider>
  )
}

export default OperationsBatchDetail
